import { Injectable, inject } from '@angular/core';

import { QuestionStateService } from '@core/services';

import { APIModel } from '@shared/interfaces';

import { Language } from '@/organisations/models';

import { exportFraction } from '../utils';
import { QuestionFieldCondition } from './question-field-condition.model';
import { QuestionFieldLabel } from './question-field-label.model';
import { QuestionFieldOptions } from './question-field-options.model';
import { QuestionType } from './question-type.enum';
import { NestedQuestion } from './question.model';

const AUTOCOMPLETE_TAGS = {
    [QuestionType.Email]: 'email',
    [QuestionType.Telephone]: 'tel',
    [QuestionType.Url]: 'url',
    [QuestionType.Password]: 'current-password',
    [QuestionType.Date]: 'bday',
};

export class QuestionField implements APIModel {
    id = -1;
    order = 0;
    conditions?: QuestionFieldCondition[];
    labels = [] as QuestionFieldLabel[];
    localizedLabel = '';
    options?: QuestionFieldOptions[];
    selectDependentDisplayField?: number;
    localizedOptions?: { key: string; value: string }[];
    nestedQuestion?: NestedQuestion;
    tag = '';
    autocompleteTag = 'off';
    type: QuestionType = QuestionType.Text;
    required = true;
    readOnly = false;
    syncableToSilverfin = false;
    readOnlyOrNonSyncable = false;

    localize(language: Language) {
        if (this.labels.length > 0) {
            this.localizedLabel =
                this.labels.find((label) => label.language == language)?.text ??
                this.labels.find((label) => label.language == Language.NL)?.text ??
                this.labels[0].text;
        }

        if ((this.options?.length ?? 0) > 0) {
            const selectedOptions =
                this.options!.find((opt) => opt.language == language) ??
                this.options!.find((opt) => opt.language == Language.NL) ??
                this.options![0];
            this.localizedOptions = [];
            selectedOptions.values.forEach((v, i) => {
                if (i < selectedOptions.text.length) {
                    this.localizedOptions?.push({ key: selectedOptions.text[i], value: v });
                }
            });
            if (selectedOptions.sorted) {
                this.localizedOptions.sort((o1, o2) => o1.key.localeCompare(o2.key, language));
            }
        }
        if (this.nestedQuestion !== undefined) {
            this.nestedQuestion.localize(language);
        }
    }

    deserialize(data: any) {
        this.id = data.id ?? -1;
        this.order = data.order ?? 0;
        this.syncableToSilverfin = data.syncable_to_silverfin ?? false;
        this.conditions =
            (data.conditions?.length ?? 0) > 0
                ? data.conditions.map((cond: any) => new QuestionFieldCondition().deserialize(cond))
                : undefined;
        this.labels = data.labels?.map((label: any) => new QuestionFieldLabel().deserialize(label)) ?? [];
        this.options = data.options?.map((opt: any) => new QuestionFieldOptions().deserialize(opt)) ?? undefined;
        this.nestedQuestion = data.nested_question !== null ? new NestedQuestion().deserialize(data.nested_question) : undefined;
        this.selectDependentDisplayField = data.select_dependent_display_field ?? undefined;
        this.tag = data.tag ?? '';
        this.type = data.type ?? QuestionType.Text;
        this.autocompleteTag = Object.keys(AUTOCOMPLETE_TAGS).includes(this.type)
            ? AUTOCOMPLETE_TAGS[this.type as keyof typeof AUTOCOMPLETE_TAGS]
            : 'off';
        this.required = data.required ?? true;
        this.readOnly = data.read_only ?? false;
        this.syncableToSilverfin = data.syncable_to_silverfin ?? false;
        this.readOnlyOrNonSyncable = !this.syncableToSilverfin || this.readOnly;
        return this;
    }

    isActivatedBy(form: any, allFields: QuestionField[], external?: boolean, stateService?: QuestionStateService): boolean {
        let result: undefined | boolean = undefined;
        this.conditions?.forEach((condition) => {
            const dependsOnField = allFields.find((qField) => qField.id === condition.dependsOn);
            const dependsOnTag = dependsOnField?.tag;
            let dependsOnValue =
                dependsOnTag !== undefined
                    ? form.get(dependsOnTag)?.value
                    : external ?? false
                    ? stateService?.getCurrentFieldValueExternal(condition.dependsOn)
                    : stateService?.getCurrentFieldValue(condition.dependsOn);
            if (dependsOnField?.type === QuestionType.Fraction && dependsOnValue) {
                dependsOnValue = exportFraction(dependsOnValue);
            }
            const conditionResult: boolean = condition.checkCondition(dependsOnValue);

            if (result === undefined) {
                result = conditionResult;
            } else if (condition.logicalOperator === 'and') {
                result = result && conditionResult;
            } else if (condition.logicalOperator === 'or') {
                result = result || conditionResult;
            }
        });
        return result ?? true;
    }
}
